import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router'

import { getApplication, listCredentials } from '../../api/portal'
import { usePortal } from '../../auth/sessionContext'
import { CopyableId } from '../../components/ui/CopyableId'
import { DataState } from '../../components/DataState'
import { ApiTokensSection } from './ApiTokensSection'
import { ApplicationAccess, ApplicationRequests } from './ApplicationUsage'
import { formatDateTime } from '../../format'
import { StatusChip } from '../../components/ui/StatusChip'
import { MachineNetworksPanel } from './MachineNetworksPanel'
import { can } from '../../auth/session'

export function ApplicationDetailPage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const { applicationId = '' } = useParams()

  const application = useQuery({
    queryKey: ['client', 'application', customerId, applicationId],
    queryFn: ({ signal }) => getApplication(customerId, applicationId, signal),
  })
  const credentials = useQuery({
    queryKey: ['client', 'credentials', customerId, applicationId],
    queryFn: ({ signal }) => listCredentials(customerId, applicationId, signal),
  })

  const suspended = application.data?.status === 'suspended'

  return (
    <Box>
      <Button component={RouterLink} to="/applications" size="small" sx={{ mb: 1 }}>
        بازگشت به اپلیکیشن‌های من
      </Button>

      <DataState
        isPending={application.isPending}
        isError={application.isError}
        error={application.error}
        onRetry={() => void application.refetch()}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="h1">{application.data?.name}</Typography>
          <StatusChip status={application.data?.status} />
        </Stack>
        {suspended ? (
          <Typography variant="body2" color="text.secondary">
            این اپلیکیشن معلق است. تا زمانی که دوباره فعال نشود، فراخوانی‌های آن پذیرفته نمی‌شود.
            برای فعال‌سازی دوباره با پشتیبانی تماس بگیرید.
          </Typography>
        ) : null}
      </DataState>

      {/* The token a customer's own product is configured with. This is the
          one credential the portal issues, and the reason is in §6 of the
          product decision: making somebody file a ticket to obtain the thing
          they are paying to use would be making them wait for their own
          account. Everything else on this page is read-only. */}
      <ApiTokensSection customerId={customerId} applicationId={applicationId} />
      <MachineNetworksPanel endpoint={`/customers/${customerId}/applications/${applicationId}/networks/`}
        canEdit={can(membership, 'update_application')} />

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h2" gutterBottom>
          اعتبارنامه‌های کتابخانهٔ رسمی
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          اگر از کتابخانهٔ رسمی استفاده می‌کنید، این شناسه و رمز را ما برای شما صادر می‌کنیم. برای
          صدور، تعویض یا ابطال با پشتیبانی تماس بگیرید. برای اتصال مستقیم، توکن دسترسی بالا کافی
          است.
        </Typography>

        <DataState
          isPending={credentials.isPending}
          isError={credentials.isError}
          error={credentials.error}
          isEmpty={credentials.data?.length === 0}
          emptyMessage="برای این اپلیکیشن اعتبارنامه‌ای صادر نشده است."
          onRetry={() => void credentials.refetch()}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="اعتبارنامه‌ها">
              <TableHead>
                <TableRow>
                  <TableCell>شناسهٔ کارخواه</TableCell>
                  <TableCell>برچسب</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell>آخرین استفاده</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credentials.data?.map((credential) => (
                  <TableRow key={credential.id} hover>
                    <TableCell>
                      <CopyableId
                        value={credential.client_id}
                        visible={16}
                        label="شناسهٔ کارخواه"
                      />
                    </TableCell>
                    <TableCell>{credential.label || '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={credential.status} />
                    </TableCell>
                    <TableCell>
                      {credential.last_used_at
                        ? formatDateTime(credential.last_used_at)
                        : 'هرگز استفاده نشده'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DataState>
      </Paper>

      <ApplicationAccess customerId={customerId} applicationId={applicationId} />
      <ApplicationRequests customerId={customerId} applicationId={applicationId} />
    </Box>
  )
}
